import { AssembleeHemicycle } from "@logora/parliament.parliament_chamber.assemble_hemicycle";
import { ChamberLabel } from "@logora/parliament.parliament_chamber.chamber_label";
import { SenatHemicycle } from "@logora/parliament.parliament_chamber.senat_hemicycle";
import { MemberBox } from "@logora/parliament.parliament_member.member_box";
import { Box as UiBox } from "@logora/parliament.ui.box";
import { Label } from "@logora/parliament.ui.label";
import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/material";
import { Drawer } from "@mui/material";
import cloneDeep from "lodash/cloneDeep";
import React, { useState, useEffect, useCallback } from "react";
import { useDataProvider } from "react-admin";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AmendementShow } from "../amendements";
import Tabs from "../components/Tabs";
import { Show } from "../components/show/Show";
import {
	amendementDataset,
	documentDataset,
	dossierDataset,
	interventionDataset,
	mandatDataset,
	organeDataset,
	questionDataset,
	reunionDataset,
} from "../datasets";
import { MultiList } from "../search/MultiList";
import styles from "./OrganeShow.module.scss";

const organesTravaux = [
	"COMPER",
	"COMSENAT",
	"DELEG",
	"GP",
	"GROUPESENAT",
	"MINISTERE",
];

const OrganeDetails = ({ resource, resourceId }) => {
	const [membres, setMembres] = useState([]);
	const dataProvider = useDataProvider();
	const showTravaux = organesTravaux.includes(resource.codeType);
	const date = resource.dateDebut || resource.dateAgrement;
	const { amendementId } = useParams();
	const navigate = useNavigate();

	useEffect(() => {
		if (resource.type === "Circonscription") {
			setMembres(resource.parlementairesCirconscription);
		} else {
			fetchMembres();
		}
	}, [resource.type, resource.parlementairesCirconscription]);

	const fetchMembres = useCallback(async () => {
		try {
			const response = await dataProvider.getList("mandats", {
				filter: {
					organeRefUid: resourceId,
					actif: true,
					nominPrincipale: true,
				},
				include: ["acteurRef.groupeParlementaire"],
				pagination: { perPage: 1000 },
			});
			const membres = response.data;
			if (membres) {
				setMembres(membres);
			}
		} catch (error) {
			console.log(error);
		}
	}, [dataProvider, resourceId]);

	const president = membres?.find((m) =>
		m.codeQualite?.toLowerCase().startsWith("président"),
	);

	const attributeMemberToHemicyle = (membre) => {
		const formattedMember = {
			placeHemicycle: Number.parseInt(
				membre?.acteurRef?.placeHemicycle || membre?.placeHemicycle,
			),
			color:
				membre?.acteurRef?.groupeParlementaire?.couleurAssociee ||
				membre?.groupeParlementaire?.couleurAssociee ||
				"#777",
			prenom: membre?.acteurRef?.prenom || membre?.prenom,
			nom: membre?.acteurRef?.nom || membre?.nom,
			urlImage: membre?.acteurRef?.urlImage || membre?.urlImage,
			parliamentGroup: {
				color:
					membre?.acteurRef?.groupeParlementaire?.couleurAssociee ||
					membre?.groupeParlementaire?.couleurAssociee,
				groupName:
					membre?.acteurRef?.groupeParlementaire?.libelle ||
					membre?.groupeParlementaire?.libelle,
			},
		};
		return {
			id: formattedMember.placeHemicycle,
			color: formattedMember.color,
			labelBackgroundColor: "#FFF",
			label: (
				<Link
					className={styles.membreOrgane}
					to={`/parliament/acteurs/${membre?.acteurRef?.uid}`}
					target="_blank"
				>
					<MemberBox
						firstName={formattedMember.prenom}
						lastName={formattedMember.nom}
						imageUrl={formattedMember.urlImage}
						parliamentGroup={formattedMember.parliamentGroup}
					/>
				</Link>
			),
			position: "top",
		};
	};

	const getDatasets = useCallback(() => {
		const organesEnfantsDataset = Object.assign(cloneDeep(organeDataset), {
			filterMapping: { organeRefUid: "organeParentRefUid" },
		});

		const dossiersCommissionANDataset = Object.assign(
			cloneDeep(dossierDataset),
			{
				filterMapping: { organeRefUid: "commissionANRefUid" },
			},
		);

		const dossiersCommissionSNDataset = Object.assign(
			cloneDeep(dossierDataset),
			{
				filterMapping: { organeRefUid: "commissionSNRefUid" },
			},
		);

		const questionsMinistereDataset = Object.assign(
			cloneDeep(questionDataset),
			{
				filterMapping: { organeRefUid: "minIntRefUid" },
			},
		);

		if (resource.codeType === "DELEG") {
			return {
				documents: documentDataset,
				organes: organesEnfantsDataset,
				reunions: reunionDataset,
			};
		}
		if (resource.codeType === "COMPER" || resource.codeType === "COMSENAT") {
			return {
				dossiers:
					resource.codeType === "COMPER"
						? dossiersCommissionANDataset
						: dossiersCommissionSNDataset,
				documents: documentDataset,
				amendements: {
					...amendementDataset,
					rowRedirect: (item) =>
						`/parliament/organes/${resource.uid}/amendements/${item.uid}`,
				},
				organes: organesEnfantsDataset,
				reunions: reunionDataset,
			};
		}
		if (resource.codeType === "MINISTERE") {
			return {
				questions: questionsMinistereDataset,
			};
		}
		return {
			dossiers: dossierDataset,
			amendements: {
				...amendementDataset,
				rowRedirect: (item) =>
					`/parliament/organes/${resource.uid}/amendements/${item.uid}`,
			},
			questions: questionDataset,
			interventions: interventionDataset,
		};
	}, [resource.codeType]);

	return (
		<>
			<Box display={"flex"} flexDirection={"column"}>
				<Box
					className={styles.avatarBackground}
					display={"flex"}
					gap={"2em"}
					sx={{ marginTop: "1em" }}
				>
					{resource.urlImage && (
						<div className={styles.imageWrapper}>
							<img className={styles.image} src={resource.urlImage} alt="" />
						</div>
					)}
					<Box
						display="flex"
						flexDirection={"column"}
						marginBottom={"1em"}
						gap={"0.5em"}
					>
						<div className={styles.header}>
							{resource.chambre && <ChamberLabel chamber={resource.chambre} />}
							<Label text={resource.type} />
						</div>
						<div className={styles.title}>{resource.libelle}</div>
						<div className={styles.subtitle}>
							{`Créé le ${new Date(date).toLocaleDateString("fr-FR", {
								year: "numeric",
								month: "long",
								day: "numeric",
							})} et ${
								resource.actif ? "toujours en activité" : "inactif aujourd'hui"
							}`}
						</div>
					</Box>
				</Box>
				<div className={styles.presentation}>
					<Box
						display="grid"
						gridTemplateColumns="1fr 1fr"
						gap="0.5em"
						sx={{
							padding: "15px",
							width: "100%",
						}}
					>
						<Box display="flex" flexDirection="column" gap="1em">
							{resource.positionPolitique && (
								<Box display="flex" flexDirection="column">
									<UiBox className={styles.uiBox} title="Position">
										{resource.positionPolitique}
									</UiBox>
								</Box>
							)}
							{resource.nombreMembres > 0 && (
								<Box display="flex" flexDirection="column">
									<UiBox className={styles.uiBox} title="Effectif">
										<>{resource.nombreMembres} membres</>
									</UiBox>
								</Box>
							)}
							{resource.organeParentRef && (
								<Box display="flex" flexDirection="column">
									<UiBox className={styles.uiBox} title="Appartenance">
										{resource.organeParentRef.libelle}
									</UiBox>
								</Box>
							)}
							{president?.acteurRef && (
								<Box display="flex" flexDirection="column">
									<UiBox className={styles.uiBox} title="Présidence">
										<Link
											className={styles.memberLink}
											to={`/parliament/acteurs/${president?.acteurRef?.uid}`}
											target="_blank"
										>
											<MemberBox
												firstName={president?.acteurRef.prenom}
												lastName={president?.acteurRef.nom}
												imageUrl={president?.acteurRef.urlImage}
											/>
										</Link>
									</UiBox>
								</Box>
							)}
							{resource.parlementairesCirconscription &&
								resource.parlementairesCirconscription.length > 0 && (
									<Box display="flex" flexDirection="column">
										<UiBox
											className={styles.uiBox}
											title="Parlementaire actuel"
										>
											<Link
												className={styles.memberLink}
												to={`/parliament/acteurs/${resource.parlementairesCirconscription[0].uid}`}
												target="_blank"
											>
												<MemberBox
													firstName={
														resource.parlementairesCirconscription[0].prenom
													}
													lastName={
														resource.parlementairesCirconscription[0].nom
													}
													imageUrl={
														resource.parlementairesCirconscription[0].urlImage
													}
												/>
											</Link>
										</UiBox>
									</Box>
								)}
						</Box>
						<Box flex={2} className={styles.svgContainer}>
							{resource.chambre === "AN" && (
								<AssembleeHemicycle
									className={styles.svgIcon}
									highlightedSeats={membres?.map(attributeMemberToHemicyle)}
								/>
							)}
							{resource.chambre === "SN" && (
								<SenatHemicycle
									highlightedSeats={membres?.map(attributeMemberToHemicyle)}
								/>
							)}
						</Box>
					</Box>
				</div>
			</Box>
			<div className={styles.corps}>
				<Box sx={{ width: "100%" }}>
					<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
						<Tabs
							tabData={[
								showTravaux && {
									label:
										resource.codeType === "MINISTERE" ? "Questions" : "Travaux",
									content: (
										<div role="tabpanel" value={0} index={0}>
											<MultiList
												datasets={getDatasets()}
												showListSearch={true}
												globalHiddenFilters={{
													organeRefUid: resource.uid,
												}}
											/>
										</div>
									),
								},
								{
									label: "Composition",
									content: (
										<div role="tabpanel" value={1} index={1}>
											<MultiList
												datasets={{
													mandats: mandatDataset,
												}}
												globalHiddenFilters={{
													organeRefUid: resource.uid,
												}}
												showListSearch={true}
											/>
										</div>
									),
								},
							].filter(Boolean)}
						/>
					</Box>
				</Box>
			</div>
			<Drawer
				open={!!amendementId}
				onClose={() => {
					navigate(`/parliament/organes/${resource.uid}`);
				}}
				anchor="right"
				sx={{ zIndex: 100 }}
			>
				<Show
					resourceId={amendementId}
					resource="amendements"
					includes={[
						"acteurRef.groupeParlementaire",
						"acteurRef.mandatPrincipal",
						"documentRef.dossierRef",
						"gouvernementOrganeRef",
					]}
					backIcon={<CloseIcon />}
					backText={"Fermer"}
				>
					<AmendementShow />
				</Show>
			</Drawer>
		</>
	);
};

export const OrganeShow = () => {
	return (
		<Show
			resource={"organes"}
			includes={["organeParentRef", "parlementairesCirconscription"]}
		>
			<OrganeDetails />
		</Show>
	);
};
